import { secureApi } from './secureApi';

// Thin wrapper over the GuestRequest backend endpoints (Guest Requests Hub V1).
// All endpoints are JWT-authenticated via secureApi (x-auth-token header).
//
// Response envelopes (per locked plan, matches bookings/properties pattern):
//   List:   { success: true, requests: [...], count: N }
//   Single: { success: true, request: {...} }
//
// Backend hands display fields ready-to-render (displayPropertyName,
// displayGuestName, linkedTaskTicketId, linkedTaskStatus) — frontend never
// traverses populated refs.
//
// V1 filter scope is intentionally minimal: only bookingId + guestId on list().
// status/priority/assignedTo filters land in V1.2+ when an ops list view exists.
//
// GET    /api/v1/guest-requests?bookingId=X|guestId=Y  → list
// GET    /api/v1/guest-requests/open?propertyId=X      → V1.1 open-requests triage queue
// GET    /api/v1/guest-requests/:id                    → single
// POST   /api/v1/guest-requests                        → create (transactional with optional Task)
// PATCH  /api/v1/guest-requests/:id                    → update fields
// PATCH  /api/v1/guest-requests/:id/status             → manual status update (sets *Via='manual')
// POST   /api/v1/guest-requests/:id/notes              → append to activity log
// DELETE /api/v1/guest-requests/:id                    → admin soft-delete (status → cancelled)
const guestRequestApi = {
  list: async ({ bookingId, guestId } = {}) => {
    const params = {};
    if (bookingId) params.bookingId = bookingId;
    if (guestId) params.guestId = guestId;
    const response = await secureApi.get('/api/v1/guest-requests', { params });
    return response.data;
  },

  listOpen: async ({ propertyId } = {}) => {
    const params = {};
    if (propertyId) params.propertyId = propertyId;
    const response = await secureApi.get('/api/v1/guest-requests/open', { params });
    return response.data;
  },

  // V1.1 — atomically create a Task and link it to an existing GR.
  // Backend mirrors the create-with-Task transaction. Returns the decorated GR.
  linkTask: async (id, payload) => {
    const response = await secureApi.post(`/api/v1/guest-requests/${id}/link-task`, payload);
    return response.data;
  },

  get: async (id) => {
    const response = await secureApi.get(`/api/v1/guest-requests/${id}`);
    return response.data;
  },

  create: async (payload) => {
    const response = await secureApi.post('/api/v1/guest-requests', payload);
    return response.data;
  },

  update: async (id, payload) => {
    const response = await secureApi.patch(`/api/v1/guest-requests/${id}`, payload);
    return response.data;
  },

  updateStatus: async (id, payload) => {
    const response = await secureApi.patch(`/api/v1/guest-requests/${id}/status`, payload);
    return response.data;
  },

  addNote: async (id, payload) => {
    const response = await secureApi.post(`/api/v1/guest-requests/${id}/notes`, payload);
    return response.data;
  },

  softDelete: async (id) => {
    const response = await secureApi.delete(`/api/v1/guest-requests/${id}`);
    return response.data;
  }
};

export default guestRequestApi;
